import * as React from "react";
import type { Subtract } from "utility-types";

import type { TextFieldViewProps } from "../../views/text-field/text-field-view";
import TextFieldView from "../../views/text-field/text-field-view";

interface ExtraProps {
    onChange: (newVal: string) => void;
    validate?: (input: string) => string | undefined;
}

interface Handled {
    onChange?: (ev: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => void;
    focused?: boolean;
    errorMessage?: string;
    onFocus?: () => void;
    onBlur?: () => void;
}

type Props = Subtract<TextFieldViewProps, Handled> & ExtraProps;

const TextField: React.FunctionComponent<Props> = props => {
    const { value, onChange, validate, ...rest } = props;

    const [text, setText] = React.useState(value);
    const [lastProps, setLastProps] = React.useState(value);
    const [focused, setFocused] = React.useState(false);
    const [errorMessage, setErrorMessage] = React.useState<string | undefined>(undefined);

    React.useEffect(() => {
        if (lastProps !== value) {
            if (!focused) {
                setText(value);
            }
            setLastProps(value);
        }
    }, [lastProps, value, focused]);

    const handleChange = React.useCallback(
        (ev: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
            const t = ev.target.value;

            setText(t);
            if (onChange !== undefined) {
                onChange(t);
            }

            if (errorMessage !== undefined && validate !== undefined) {
                const error = t === "" ? undefined : validate(t);
                setErrorMessage(error);
            }
        },
        [onChange, validate, errorMessage]
    );

    const onFocus = React.useCallback(() => {
        setFocused(true);
    }, []);

    const onBlur = React.useCallback(() => {
        setFocused(false);
        if (validate !== undefined) {
            const error = text === "" ? undefined : validate(text);
            setErrorMessage(error);
        }
    }, [validate, text]);

    return (
        <TextFieldView
            value={text}
            onFocus={onFocus}
            onBlur={onBlur}
            focused={focused}
            onChange={handleChange}
            errorMessage={errorMessage}
            {...rest}
        />
    );
};

export default TextField;
